import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';

interface NavigationContextType {
  isBlocked: boolean;
  setBlocked: (blocked: boolean) => void;
  navigateWithBlocking: (path: string) => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  pendingPath: string | null;
  showConfirmation: boolean;
  setShowConfirmation: (show: boolean) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();
  const isBlockedRef = useRef(false);

  const setBlocked = useCallback((blocked: boolean) => {
    isBlockedRef.current = blocked;
  }, []);

  const navigateWithBlocking = useCallback(
    (path: string) => {
      if (isBlockedRef.current) {
        setPendingPath(path);
        setShowConfirmation(true);
      } else {
        navigate(path);
      }
    },
    [navigate],
  );

  const confirmNavigation = useCallback(() => {
    const pathToNavigate = pendingPath;

    // Clear state first to avoid timing issues
    setPendingPath(null);
    setShowConfirmation(false);

    // Then navigate
    if (pathToNavigate) {
      navigate(pathToNavigate);
    }
  }, [navigate, pendingPath]);

  const cancelNavigation = useCallback(() => {
    setPendingPath(null);
    setShowConfirmation(false);
  }, []);

  // Handle browser beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isBlockedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const value: NavigationContextType = {
    isBlocked: isBlockedRef.current,
    setBlocked,
    navigateWithBlocking,
    confirmNavigation,
    cancelNavigation,
    pendingPath,
    showConfirmation,
    setShowConfirmation,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
